import { Trans, useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import {
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Flex,
  Form,
  Input,
  Popconfirm,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Steps,
  Switch,
  Table,
  Tooltip,
} from 'antd';
import { DeleteOutlined, InfoCircleOutlined, UploadOutlined } from '@ant-design/icons';
import { collect } from 'collect.js';
import classnames from 'classnames';
import useHistory from '../../hooks/navigation/useHistory';
import CardWrapper from '../../components/cards/CardWrapper';
import LoadingScreen from '../../components/loading/LoadingScreen';
import TestRestaurantTag from '../../components/provider-billing/TestRestaurantTag';
import useForm from '../../hooks/utility/useForm';
import useApiClient from '../../hooks/api/useApiClient';
import useFeedbackLabel from '../../hooks/ui/useFeedbackLabel';
import { BusinessType, ENVIRONMENTS } from '../../services/exports/Constants';
import PhoneNumberInput from '../../components/form/general/PhoneNumberInput';
import ReduxHooks from '../../store/ReduxHooks';
import { actionCreators } from '../../store/actions';
import useHelpers from '../../hooks/context/useHelpers';
import FranchiseSelect from '../../components/form/business/FranchiseSelect';
import useScreenType from '../../hooks/context/useScreenType';
import AddressHelpers from '../../services/helpers/AddressHelpers';
import BusinessProfileSelect from '../../components/form/business/BusinessProfileSelect';
import theme from '../../styles/Theme';
import AddressFields from '../../components/form/address/AddressFields';
import AddCompanyModal from '../../modals/company/add-company-modal';
import CompanyCard from '../../components/company/CompanyCard';
import { ScreenEnum } from '../../hooks/navigation/Screens';
import usePlgOnboarding from '../../hooks/auth/usePlgOnboarding';
import useOpenOnboarding from '../../hooks/onboarding/useOpenOnboarding';
import { isPlgOnboardingProvider } from '../../services/tenant/plgOnboarding';
import {
  getsBusinessTypeInGetStarted,
  isNewBusinessProfileComplete,
  leavesLegalDetailsToOwner,
  newBusinessProfilePayload,
} from './newBusinessProfile';

export const CompanyCreationModes = {
  demo: 'demo',
  full: 'full',
};

export const CompanyCreationSteps = {
  CompanyInfo: 'company_info',
  BusinessInfo: 'business_info',
  AdditionalLocations: 'additional_locations',
};

export const FranchiseOption = {
  CreateFranchise: 'create_franchise',
  UseExistingFranchise: 'use_existing_franchise',
};

export const BusinessProfileOption = {
  CreateBusinessProfile: 'create_business_profile',
  UseExistingBusinessProfile: 'use_existing_business_profile',
};

function Instruction({ children, className }) {
  return (
    <div className={classnames('mb-md border-solid border-0 border-l-[5px] border-brand-warning pl-sm', className)}>
      <p>{children}</p>
    </div>
  );
}

function CompaniesList({ data, mode, showAddButton, update, className }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:Companies' });

  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);
  const isPlg = usePlgOnboarding();
  const history = useHistory();
  // One onboarding opener for the whole grid (see CompanyCard.onContinueSetup).
  const { open: openOnboarding } = useOpenOnboarding();

  const [selectedCompanyIndex, setSelectedCompanyIndex] = useState(null);
  const [showCompanyModal, setShowCompanyModal] = useState(false);

  const toggleCompanyModal = (index = null) => {
    setSelectedCompanyIndex(index);
    setShowCompanyModal((current) => !current);
  };

  // Partner-only entry swap (FOO-6351): partners (non-master providers) with
  // the `plg_onboarding` feature flag start a new restaurant in the full-screen
  // onboarding wizard; masters and non-flagged partners keep the legacy
  // AddCompanyModal for the batch-create / classic flow.
  const addRestaurant = () => {
    if (isPlg) {
      history.push(ScreenEnum.NewRestaurant);

      return;
    }

    toggleCompanyModal();
  };

  const onSaveNewCompany = (companyData) => {
    // Only a partner's full form starts from the restaurant's Google data: on a Foodamigos
    // restaurant the owner types the legal details, so none may be filled in for them.
    if (data.companies.length === 0 && !leavesLegalDetailsToOwner(serviceProvider)) {
      update('business_profile', {
        ...data.business_profile,

        legal_name: companyData.name,
        display_name: companyData.name,

        legal_address: companyData.address,
        legal_city: companyData.city,
        legal_state: companyData.state,
        legal_country: companyData.country,
        legal_zip_code: companyData.zip_code,

        owner_first_name: null,
        owner_last_name: null,
        owner_email: null,
        owner_phone_number: null,
      });
    }

    if (selectedCompanyIndex === null) {
      return update('companies', [...data.companies, companyData]);
    }

    return update('companies', collect(data.companies).put(selectedCompanyIndex, companyData).toArray());
  };

  return (
    <>
      <h6 className={classnames('underline ml-md mb-md', className)}>{t('form.restaurants.label')}</h6>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 bg-brand-inkGrey-grey_0 px-sm py-md">
        {data.franchise_option === FranchiseOption.UseExistingFranchise &&
          (data.franchise?.companies ?? []).map((item) => (
            <CompanyCard
              key={item.id}
              company={item}
              showOnboardingStatus={isPlg}
              onContinueSetup={isPlg ? openOnboarding : null}
            />
          ))}
        {data.companies.map((item, index) => (
          <Badge.Ribbon key={index} text={<UploadOutlined />} color={theme.color.brand.warning} placement="start">
            <CompanyCard
              company={item}
              isDraft
              onEdit={() => toggleCompanyModal(index)}
              onDelete={() => update('companies', collect(data.companies).forget(index).values().toArray())}
            />
          </Badge.Ribbon>
        ))}
        {showAddButton && (
          <Card
            variant="borderless"
            classNames={{ body: 'h-full flex items-center cursor-pointer !py-xl' }}
            onClick={addRestaurant}
          >
            <p className="m-auto text-center font-semibold">{t('buttons.add_restaurant')}</p>
          </Card>
        )}
      </div>
      {showCompanyModal && (
        <AddCompanyModal
          isDemo={mode === CompanyCreationModes.demo}
          company={selectedCompanyIndex !== null ? data.companies[selectedCompanyIndex] : null}
          onSave={onSaveNewCompany}
          onClose={toggleCompanyModal}
        />
      )}
    </>
  );
}

function CompanyInfoStep({ data, update, mode, renderFeedbackLabel }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:Companies' });

  return (
    <>
      <Form layout="vertical" className="p-md pb-none">
        {renderFeedbackLabel}
        <Form.Item label={<span className="font-semibold text-base">{t('form.franchise.label')}</span>}>
          <Instruction>{t('form.franchise.tooltip')}</Instruction>
          <Radio.Group value={data.franchise_option} onChange={(e) => update('franchise_option', e.target.value)}>
            <Space orientation="vertical">
              <Radio value={FranchiseOption.CreateFranchise}>
                {t(`form.franchise.options.${FranchiseOption.CreateFranchise}.title`)}
                <Tooltip title={t(`form.franchise.options.${FranchiseOption.CreateFranchise}.tooltip`)}>
                  <InfoCircleOutlined className="ml-xs" />
                </Tooltip>
              </Radio>
              <Radio value={FranchiseOption.UseExistingFranchise}>
                {t(`form.franchise.options.${FranchiseOption.UseExistingFranchise}.title`)}
                <Tooltip title={t(`form.franchise.options.${FranchiseOption.UseExistingFranchise}.tooltip`)}>
                  <InfoCircleOutlined className="ml-xs" />
                </Tooltip>
              </Radio>
            </Space>
          </Radio.Group>
          {data.franchise_option === FranchiseOption.UseExistingFranchise && (
            <FranchiseSelect
              value={data.franchise?.id ? data.franchise : null}
              onChange={(value) => update('franchise', value)}
              className="ml-[20px] mt-xs w-fit"
            />
          )}
        </Form.Item>
      </Form>
      <CompaniesList
        mode={mode}
        data={data}
        showAddButton={data.companies.length === 0 || mode === CompanyCreationModes.demo}
        update={update}
      />
    </>
  );
}

function BusinessInfoStep({ data, update, renderFeedbackLabel }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:Companies' });

  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);
  const ownerTypesLegalDetails = leavesLegalDetailsToOwner(serviceProvider);
  const typeAskedInGetStarted = ownerTypesLegalDetails && getsBusinessTypeInGetStarted(data.companies);

  const selectedBusinessProfileDescriptionItems = useMemo(
    () =>
      !data.business_profile.selected
        ? []
        : collect([
            { label: t('labels.business_type'), children: data.business_profile.selected.business_type },
            { label: t('labels.display_name'), children: data.business_profile.selected.display_name },
            { label: t('labels.legal_name'), children: data.business_profile.selected.legal_name },
          ])
            .when(Boolean(data.business_profile.selected.owner_full_name), (collection) =>
              collection.push({
                label: t('labels.owner_name'),
                children: data.business_profile.selected.owner_full_name,
              }),
            )
            .merge([
              {
                label: t('labels.address'),
                // legal_country can differ from the provider's operating country for a
                // cross-border legal entity; the provider's own address_format is the
                // best available signal without a client-side country->format table.
                children: AddressHelpers.format(
                  {
                    street_name: data.business_profile.selected.legal_address,
                    city: data.business_profile.selected.legal_city,
                    state: data.business_profile.selected.legal_state,
                    zip_code: data.business_profile.selected.legal_zip_code,
                  },
                  serviceProvider?.regional_config?.address_format ?? 'international',
                ),
              },
            ])
            // A profile created since PROD-557 has no legal name or address until the owner adds them.
            .filter((item) => Boolean(item.children))
            .toArray(),
    [data.business_profile.selected, serviceProvider?.regional_config?.address_format],
  );

  return (
    <Form layout="vertical" className="p-md">
      {renderFeedbackLabel}
      <Form.Item
        label={<span className="font-semibold text-base">{t('form.business_profile.label')}</span>}
        className="!mb-none"
      >
        <Instruction>
          <Trans t={t} components={{ br: <br /> }}>
            form.business_profile.tooltip
          </Trans>
        </Instruction>
        <Radio.Group
          value={data.business_profile_option}
          onChange={(e) => update('business_profile_option', e.target.value)}
        >
          <Space orientation="vertical">
            <Radio value={BusinessProfileOption.CreateBusinessProfile}>
              {t(`form.business_profile.options.${BusinessProfileOption.CreateBusinessProfile}`)}
            </Radio>
            <Radio value={BusinessProfileOption.UseExistingBusinessProfile}>
              {t(`form.business_profile.options.${BusinessProfileOption.UseExistingBusinessProfile}`)}
            </Radio>
          </Space>
        </Radio.Group>
      </Form.Item>
      <div className="ml-[20px] mt-xs mb-md">
        {data.business_profile_option === BusinessProfileOption.CreateBusinessProfile && (
          <>
            {/* A German restaurant of ours gets the business type from the legal form in Get Started (PROD-836). */}
            {!typeAskedInGetStarted && (
              <>
                <h6 className="mb-md">{t('sections.business_profile.legal_info')}</h6>
                <Form.Item label={t('form.business_type.label')} required>
                  <Select
                    value={data.business_profile.business_type}
                    onChange={(value) => update('business_profile.business_type', value)}
                    placeholder={t('form.business_type.placeholder')}
                    options={Object.values(BusinessType).map((item) => ({
                      label: t(`form.business_type.options.${item}`),
                      value: item,
                    }))}
                  />
                </Form.Item>
              </>
            )}
            {!ownerTypesLegalDetails && (
              <>
                <Form.Item label={t('form.display_name.label')} required>
                  <Input
                    value={data.business_profile.display_name}
                    onChange={(e) => update('business_profile.display_name', e.target.value)}
                    placeholder={t('form.display_name.placeholder')}
                  />
                </Form.Item>
                <Form.Item label={t('form.legal_name.label')} required>
                  <Input
                    value={data.business_profile.legal_name}
                    onChange={(e) => update('business_profile.legal_name', e.target.value)}
                    placeholder={t('form.legal_name.placeholder')}
                  />
                </Form.Item>
                <AddressFields
                  useGrid
                  prefix="legal"
                  data={data.business_profile}
                  onChange={(field, value) => update(`business_profile.${field}`, value)}
                />
              </>
            )}
            <h6 className="mb-md">{t('sections.business_profile.owner_info')}</h6>
            {!ownerTypesLegalDetails && (
              <>
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item label={t('form.owner_first_name.label')} required>
                      <Input
                        value={data.business_profile.owner_first_name}
                        autocomplete={null}
                        onChange={(e) => update('business_profile.owner_first_name', e.target.value)}
                        placeholder={t('form.owner_first_name.placeholder')}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label={t('form.owner_last_name.label')} required>
                      <Input
                        value={data.business_profile.owner_last_name}
                        autocomplete={null}
                        onChange={(e) => update('business_profile.owner_last_name', e.target.value)}
                        placeholder={t('form.owner_last_name.placeholder')}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item label={t('form.owner_phone_number.label')} required>
                  <PhoneNumberInput
                    defaultCountry={serviceProvider.country}
                    value={data.business_profile.owner_phone_number ?? ''}
                    onChange={(value) => update('business_profile.owner_phone_number', value)}
                    international
                    className="w-full font-sm relative"
                    countryCallingCodeEditable={false}
                  />
                </Form.Item>
              </>
            )}
            <Form.Item label={t('form.owner_email.label')} required>
              <Input
                value={data.business_profile.owner_email}
                autoComplete="off"
                onChange={(e) => update('business_profile.owner_email', e.target.value)}
                placeholder={t('form.owner_email.placeholder')}
                type="email"
              />
            </Form.Item>
            <Form.Item label={t('form.password.label')} required>
              <Input.Password
                autoComplete="new-password"
                onChange={(e) => update('business_profile.password', e.target.value)}
                placeholder={t('form.password.placeholder')}
                type="password"
              />
            </Form.Item>
          </>
        )}
        {data.business_profile_option === BusinessProfileOption.UseExistingBusinessProfile && (
          <>
            <BusinessProfileSelect
              value={data.business_profile.selected}
              onChange={(value) => update('business_profile.selected', value)}
              className="w-fit"
            />
            {data.business_profile.selected && (
              <Descriptions className="mt-md" items={selectedBusinessProfileDescriptionItems} />
            )}
          </>
        )}
      </div>
    </Form>
  );
}

function AdditionalLocationsStep({ data, update, renderFeedbackLabel }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:Companies' });

  const company = data.companies[0]?.name ?? '';

  return (
    <>
      <Form layout="vertical" className="p-md">
        {renderFeedbackLabel}
        <Form.Item
          label={<span className="font-semibold text-base">{t('form.additional_companies.label')}</span>}
          className="mb-none"
        >
          <Instruction>
            <Trans t={t} values={{ company }} components={{ br: <br /> }}>
              form.additional_companies.tooltip
            </Trans>
          </Instruction>
        </Form.Item>
      </Form>
      <CompaniesList data={data} showAddButton update={update} />
    </>
  );
}

export default function CompaniesView() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:Companies' });

  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);

  const { CompaniesManager } = useApiClient();
  const { setFeedback, renderFeedbackLabel } = useFeedbackLabel({ className: 'mb-md' });
  const { formatDateTime } = useHelpers();
  const { isDesktop } = useScreenType();
  const history = useHistory();

  const [mode, setMode] = useState(
    import.meta.env.VITE_APP_MODE === ENVIRONMENTS.PROD ? CompanyCreationModes.full : CompanyCreationModes.demo,
  );
  const [currentStep, setCurrentStep] = useState(0);
  const isFinalStep = mode === CompanyCreationModes.demo || currentStep === 2;

  const { data, update } = useForm({
    franchise_option: null,
    business_profile_option: null,

    franchise: {
      id: null,
      companies: [],
    },
    business_profile: {
      selected: null,
      business_type: null,
      legal_name: null,
      display_name: null,

      legal_address: null,
      legal_city: null,
      legal_state: null,
      legal_country: null,
      legal_zip_code: null,

      owner_first_name: null,
      owner_last_name: null,
      owner_email: null,
      owner_phone_number: null,
      password: null,
    },
    companies: [],
  });

  const isStepComplete = useMemo(() => {
    // Drafts added while the demo toggle was on lack the fields the backend
    // requires for real restaurants — re-check them against the current mode.
    const isEveryDraftCompleteForMode =
      mode === CompanyCreationModes.demo ||
      data.companies.every((item) => Boolean(item.restaurant_type) && (item.cuisine_types ?? []).length > 0);

    const isRestaurantInfoStepComplete =
      data.companies.length > 0 &&
      isEveryDraftCompleteForMode &&
      Boolean(data.franchise_option) &&
      (data.franchise_option === FranchiseOption.CreateFranchise || data.franchise.id !== null);

    const isBusinessInfoStepComplete =
      (data.business_profile_option === BusinessProfileOption.UseExistingBusinessProfile &&
        Boolean(data.business_profile.selected)) ||
      (data.business_profile_option === BusinessProfileOption.CreateBusinessProfile &&
        isNewBusinessProfileComplete(data.business_profile, serviceProvider, data.companies));

    return {
      0: isRestaurantInfoStepComplete,
      1: isBusinessInfoStepComplete,
      2: isRestaurantInfoStepComplete && isBusinessInfoStepComplete,
    }[currentStep];
  }, [data, currentStep, mode, serviceProvider]);

  const [companies, setCompanies] = useState({ data: [] });
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  // Set synchronously: `creating` is stale for a second click before the re-render.
  const isCreatingRef = useRef(false);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    loadCompanies();
  }, [serviceProvider?.id]);

  const createCompanies = async () => {
    if (isCreatingRef.current) {
      return;
    }

    isCreatingRef.current = true;
    setCreating(true);
    const { success, data: responseData } = await CompaniesManager.create(
      collect({
        is_demo: mode === CompanyCreationModes.demo,
        franchise_id: data.franchise_option === FranchiseOption.UseExistingFranchise ? data.franchise.id : null,
        companies: collect(data.companies).map((item) => ({
          ...item,
          google_places_id: item.google_place?.place_id ?? null,
          company_info: mode === CompanyCreationModes.demo ? null : item.company_info,
        })),
      })
        .when(
          data.business_profile_option === BusinessProfileOption.UseExistingBusinessProfile,
          (collection) =>
            mode === CompanyCreationModes.demo
              ? collection
              : collection.put('business_profile_id', data.business_profile.selected.id),
          (collection) =>
            mode === CompanyCreationModes.demo
              ? collection
              : collection.put(
                  'business_profile',
                  newBusinessProfilePayload(data.business_profile, serviceProvider, data.companies),
                ),
        )
        .all(),
    );
    isCreatingRef.current = false;
    setCreating(false);

    if (!success) {
      toast.error(t('toasts.failed_to_create_company'));

      return setFeedback({
        message: responseData?.message,
        type: 'error',
        errors: responseData?.errors,
      });
    }

    toast.success(t('toasts.company_created'));

    ReduxHooks.dispatch(actionCreators.currentCompany.set(responseData));

    setCompanies((current) => ({
      ...current,
      data: [responseData, ...(current?.data ?? [])],
    }));

    // A PLG restaurant onboards through the wizard; Get Started is the legacy KYC stepper.
    mode !== CompanyCreationModes.demo &&
      history.push(isPlgOnboardingProvider(serviceProvider) ? ScreenEnum.RestaurantOnboarding : ScreenEnum.GetStarted);
  };

  const loadCompanies = async (page) => {
    setLoading(true);
    const { success, data: responseData } = await CompaniesManager.get({
      page,
    });
    setLoading(false);

    if (!success) {
      return toast.error(t('toasts.failed_to_load_recently_created_companies'));
    }

    setCompanies(responseData);
  };

  const deleteCompany = async (id) => {
    if (import.meta.env.VITE_APP_MODE === ENVIRONMENTS.PROD) {
      return;
    }

    setDeleting(id);
    const { success } = await CompaniesManager.delete(id);
    setDeleting(null);

    if (!success) {
      return toast.error(t('toasts.failed_to_delete_demo'));
    }

    setCompanies((current) => ({
      ...current,
      data: collect(current.data).where('id', '!=', id).toArray(),
    }));

    return toast.success(t('toasts.demo_got_deleted'));
  };

  const onProceed = () => {
    if (isFinalStep) {
      return createCompanies();
    }

    setCurrentStep((current) => current + 1);
  };

  const renderContent = () => (
    <div>
      <h2>{t('title')}</h2>
      <CardWrapper
        containerClassName="mt-md"
        className="!p-none"
        header={
          <div className="w-full flex items-center justify-between">
            <h6>{t(`sections.new_company.titles.${mode}`)}</h6>
            {import.meta.env.VITE_APP_MODE !== ENVIRONMENTS.PROD && (
              <Flex>
                <Switch
                  checked={mode === CompanyCreationModes.demo}
                  onChange={(checked) => setMode(checked ? CompanyCreationModes.demo : CompanyCreationModes.full)}
                  id="is-demo"
                />
                <label htmlFor="is-demo" className="ml-xs my-auto">
                  {mode === CompanyCreationModes.demo ? t('labels.create_demo') : t('labels.create_company')}
                </label>
              </Flex>
            )}
          </div>
        }
        footer={
          <Flex justify="end" gap="8px">
            {mode !== CompanyCreationModes.demo && currentStep > 0 && (
              <Button onClick={() => setCurrentStep((current) => current - 1)}>{t('buttons.back')}</Button>
            )}
            <Button type="primary" loading={creating} disabled={!isStepComplete} onClick={onProceed}>
              {t(isFinalStep ? 'buttons.create' : 'buttons.continue')}
            </Button>
          </Flex>
        }
      >
        {mode !== CompanyCreationModes.demo && (
          <Steps
            className="my-md px-md max-w-[70%] mx-auto"
            current={currentStep}
            onChange={setCurrentStep}
            size={isDesktop ? 'default' : 'small'}
            items={[
              {
                title: t(`sections.new_company.steps.${CompanyCreationSteps.CompanyInfo}`),
              },
              {
                title: t(`sections.new_company.steps.${CompanyCreationSteps.BusinessInfo}`),
              },
              {
                title: t(`sections.new_company.steps.${CompanyCreationSteps.AdditionalLocations}`),
              },
            ]}
          />
        )}
        {currentStep === 0 && (
          <CompanyInfoStep
            data={data}
            mode={mode}
            update={update}
            setCurrentStep={setCurrentStep}
            renderFeedbackLabel={renderFeedbackLabel}
          />
        )}
        {currentStep === 1 && (
          <BusinessInfoStep data={data} update={update} renderFeedbackLabel={renderFeedbackLabel} />
        )}
        {currentStep === 2 && (
          <AdditionalLocationsStep data={data} update={update} renderFeedbackLabel={renderFeedbackLabel} />
        )}
      </CardWrapper>
      <div className="mt-xl">
        <h2>{t('sections.recently_created')}</h2>
        <div className="mt-lg bg-background-inkWhite-white_0 shadow rounded-lg">
          <Table
            dataSource={companies.data}
            columns={[
              {
                key: 'name',
                title: t('fields.name'),
                dataIndex: 'name',
                render: (name, record) => (
                  <>
                    {name}
                    <TestRestaurantTag company={record} className="ml-xs" />
                  </>
                ),
              },
              {
                key: 'created_at',
                title: t('fields.created_at'),
                render: (_, item) => formatDateTime(item.created_at),
              },
              {
                key: 'actions',
                title: t('fields.actions'),
                render: (_, item) => (
                  <Space size="middle">
                    {import.meta.env.VITE_APP_MODE !== ENVIRONMENTS.PROD && (
                      <Popconfirm
                        title={t('popups.delete_demo.title')}
                        icon={<DeleteOutlined />}
                        onConfirm={() => deleteCompany(item.id)}
                        okButtonProps={{
                          danger: true,
                          loading: deleting === item.id,
                        }}
                      >
                        <a className="!text-[#1677ff]">
                          {deleting === item.id ? <Spin className="mx-auto" /> : t('buttons.delete')}
                        </a>
                      </Popconfirm>
                    )}
                  </Space>
                ),
              },
            ]}
            pagination={{
              current: companies.current_page,
              total: companies.total,
              pageSize: companies.per_page,
              onChange: (page) => loadCompanies(page),
            }}
            loading={loading}
            className="rounded-none shadow-card bg-white overflow-hidden"
            rowKey={(item) => item.id}
          />
        </div>
      </div>
    </div>
  );

  return (
    <>
      {loading && <LoadingScreen />}
      {!loading && renderContent()}
    </>
  );
}
